import type { AssetGroupId } from '@infinex/asset-config';
import { USDC_BASE_ADDRESS } from '@infinex/asset-config';

import type { Activity, AirdropActivity } from '../../../types';
import { ActivityType } from '../../../types';
import { getSwidgeActivityAssetGroupId } from '../../../utils';
import { getInxOptionExerciseAssetGroupId } from '../../../utils/getInxOptionExerciseAssetGroupId';

/**
 * Determines the asset group ID for a given activity
 *
 * The function maps different activity types to their respective asset identifiers.
 * The return type is structured to ensure null is only possible for AIRDROP and AIRDROP_CLAIM activities.
 * This is because they may have pointType not assetId.
 *
 * @param activity - The activity to get the group ID for
 * @param options - Optional configuration
 * @param options.useToAsset - For activities with source/destination assets, use the destination asset
 * @returns AssetGroupId for most activity types, or null only for AIRDROP/AIRDROP_CLAIM
 */
export function getActivityAssetGroupId<T extends Activity>(
  activity: T,
  options?: {
    useToAsset?: boolean;
  }
): T extends AirdropActivity ? AssetGroupId | null : AssetGroupId {
  const result = (() => {
    switch (activity.type) {
      case ActivityType.SEND:
        return activity.assetId
          ? {
              assetId: activity.assetId,
            }
          : {
              address: activity.assetAddress,
              chain: activity.chain,
            };
      case ActivityType.SWIDGE:
        return getSwidgeActivityAssetGroupId(activity, options);
      case ActivityType.AIRDROP_CLAIM:
      case ActivityType.AIRDROP:
        if (!activity.assetId) {
          return null;
        }
        return {
          assetId: activity.assetId,
        };
      case ActivityType.PRIZE_DRAW_WIN:
        return null;
      case ActivityType.PATRON_REDEMPTION:
      case ActivityType.PATRON_PURCHASE:
        return {
          assetId: activity.assetId,
        };
      case ActivityType.EXTERNAL_APP_ACTION:
        return activity.assetId
          ? {
              assetId: activity.assetId,
            }
          : {
              address: activity.assetAddress,
              chain: activity.chain,
            };
      case ActivityType.CASHBACK_CLAIM:
        return {
          address: activity.assetAddress,
          chain: activity.chain,
        };
      case ActivityType.GAS:
        // The Gas Account is an off-chain USD ledger, so gas activities are
        // always presented as USDC regardless of the token used to fund them.
        return {
          chain: 'base',
          address: USDC_BASE_ADDRESS,
        };
      case ActivityType.SONAR_SALE_PURCHASE:
        return {
          chain: 'base',
          address: USDC_BASE_ADDRESS,
        };
      case ActivityType.INX_CLAIM:
      case ActivityType.INX_EARLY_UNLOCK:
      case ActivityType.INX_VOUCHER_REDEMPTION:
        return getInxOptionExerciseAssetGroupId(options);
      case ActivityType.PRIVATE_SEND:
      case ActivityType.PRIVATE_RECEIVE:
        return {
          address: activity.assetAddress,
          chain: activity.chain,
        };

      default: {
        const _exhaustiveCheck: never = activity;
        throw new Error(`Unhandled activity type: ${(activity as any).type}`);
      }
    }
  })();

  // Use a type assertion to enforce the contract we expect
  // This will force TypeScript to handle the types correctly at the call sites
  return result as T extends AirdropActivity
    ? AssetGroupId | null
    : AssetGroupId;
}
